import { z } from 'zod'

/**
 * Live tablet statuses. There is no `revoked` state — revoking a tablet
 * deletes the row outright. Data sets the tablet uploaded remain
 * identifiable via their captured `tabletName`.
 */
export const TabletStatusSchema = z.enum(['pending', 'approved'])
export type TabletStatus = z.infer<typeof TabletStatusSchema>

export const TabletUidSchema = z.string().min(8).max(128)

/** Request body sent by a brand-new tablet claiming a uid and a display name. */
export const TabletRegisterRequestSchema = z.object({
  tabletUid: TabletUidSchema,
  proposedName: z.string().min(1).max(120),
})

/** Admin summary of any tablet (pending or approved). */
export const TabletSchema = z.object({
  id: z.string().uuid(),
  tabletUid: TabletUidSchema,
  name: z.string(),
  status: TabletStatusSchema,
  requestedAt: z.string().datetime({ offset: true }),
  approvedAt: z.string().datetime({ offset: true }).nullable(),
  approvedBy: z.string().uuid().nullable(),
})

/**
 * The one and only time the HMAC secret is returned to an admin: immediately
 * after approval. The admin is responsible for getting it onto the tablet.
 */
export const TabletApprovalResponseSchema = z.object({
  tablet: TabletSchema,
  secret: z.string().min(32),
})

export const TabletSelfStatusSchema = z.object({
  tabletUid: TabletUidSchema,
  status: TabletStatusSchema,
  name: z.string(),
})

export const TabletPatchSchema = z.object({
  name: z.string().min(1).max(120).optional(),
})

export type TabletRegisterRequest = z.infer<typeof TabletRegisterRequestSchema>
export type Tablet = z.infer<typeof TabletSchema>
export type TabletApprovalResponse = z.infer<typeof TabletApprovalResponseSchema>
export type TabletSelfStatus = z.infer<typeof TabletSelfStatusSchema>
export type TabletPatch = z.infer<typeof TabletPatchSchema>
